import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Section from '../common/Section';
import bioIcon from '../../assets/img/bio_icon.png';
import teamClubIcon from '../../assets/img/team_club_icon.svg';
import ProfileSocialStats from './ProfileSocialStats';
import ProfileAddOnsView from '../profile/ProfileAddOnsView';

import batAvgIcon from '../../assets/img/stats/bat-avg.svg';
import bestBatsManIcon from '../../assets/img/stats/best-batsman.svg';
import bestBowlerIcon from '../../assets/img/stats/best-bowler.svg';
import bestFielderIcon from '../../assets/img/stats/best-fielder.svg';
import bestFiguresIcon from '../../assets/img/stats/best-figures.svg';
import bowlAvgIcon from '../../assets/img/stats/bowl-avg.svg';
import catchesIcon from '../../assets/img/stats/catches.svg';
import dotBallsIcon from '../../assets/img/stats/dot-balls.svg';
import economyIcon from '../../assets/img/stats/economy.svg';
import fiftiesIcon from '../../assets/img/stats/fifties.svg';
import fiveWicketIcon from '../../assets/img/stats/five-wicket.svg';
import foursIcon from '../../assets/img/stats/fours.svg';
import highestScoreIcon from '../../assets/img/stats/highest-score.svg';
import hundredsIcon from '../../assets/img/stats/hundreds.svg';
import inningsIcon from '../../assets/img/stats/innings.svg';
import maidensIcon from '../../assets/img/stats/maidens.svg';
import matchesIcon from '../../assets/img/stats/matches.svg';
import mvpIcon from '../../assets/img/stats/mvp.svg';
import potmIcon from '../../assets/img/stats/potm.svg';
import runoutsIcon from '../../assets/img/stats/runouts.svg';
import runsIcon from '../../assets/img/stats/runs.svg';
import sixesIcon from '../../assets/img/stats/sixes.svg';
import strikeRateIcon from '../../assets/img/stats/strike-rate.svg';
import stumpingsIcon from '../../assets/img/stats/stumpings.svg';
import wicketsIcon from '../../assets/img/stats/wickets.svg';

const defaultAvatar = '/avatar.png';

const AVERAGE_STAT_KEYS = new Set(['battingAverage', 'bowlingAverage']);

export const selectCanonicalProfileStats = (profile = {}) =>
  profile?.statsAuto || profile?.cricketStats || {};

export const formatProfileStatValue = (key, value) => {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
    return AVERAGE_STAT_KEYS.has(key) ? '-' : '0';
  }
  return value;
};

const STAT_GROUPS = [
  {
    title: 'Batting',
    items: [
      { key: 'matches', label: 'Matches', color: '#1f7a34', icon: '🗓', iconImage: matchesIcon, width: 18, height: 18 },
      { key: 'inningsBatted', label: 'Innings', color: '#0ea5e9', icon: '🏏', iconImage: inningsIcon, width: 18, height: 18 },
      { key: 'runs', label: 'Runs', color: '#d4002f', icon: '🏏', iconImage: runsIcon, width: 18, height: 18 },
      { key: 'highestScore', label: 'High Score', color: '#14b8a6', icon: '🚀', iconImage: highestScoreIcon, width: 18, height: 18 },
      { key: 'fours', label: 'Fours', color: '#6366f1', icon: '4️⃣', iconImage: foursIcon, width: 22, height: 22 },
      { key: 'sixes', label: 'Sixes', color: '#7c3aed', icon: '6️⃣', iconImage: sixesIcon, width: 22, height: 22 },
      { key: 'fifties', label: 'Fifties', color: '#475569', icon: '5️⃣', iconImage: fiftiesIcon, width: 22, height: 22 },
      { key: 'hundreds', label: 'Hundreds', color: '#166534', icon: '💯', iconImage: hundredsIcon, width: 22, height: 22 },
      { key: 'battingAverage', label: 'Bat Avg', color: '#b45309', icon: '📈', iconImage: batAvgIcon, width: 18, height: 18 },
      { key: 'strikeRate', label: 'Strike Rate', color: '#0369a1', icon: '⚡', iconImage: strikeRateIcon, width: 18, height: 18 },
    ],
  },
  {
    title: 'Bowling',
    items: [
      { key: 'inningsBowled', label: 'Innings Bowled', color: '#0369a1', icon: '🎯', iconImage: inningsIcon, width: 18, height: 18 },
      { key: 'wickets', label: 'Wickets', color: '#f59e0b', icon: '🎯', iconImage: wicketsIcon, width: 22, height: 22 },
      { key: 'bestFigures', label: 'Best Figures', color: '#14b8a6', icon: '📌', iconImage: bestFiguresIcon, width: 18, height: 18 },
      { key: 'economyRate', label: 'Economy', color: '#475569', icon: '📉', iconImage: economyIcon, width: 22, height: 22 },
      { key: 'bowlingAverage', label: 'Bowl Avg', color: '#6366f1', icon: '📊', iconImage: bowlAvgIcon, width: 18, height: 18 },
      { key: 'maidens', label: 'Maidens', color: '#7c3aed', icon: '🧱', iconImage: maidensIcon, width: 18, height: 18 },
      { key: 'dotBalls', label: 'Dot Balls', color: '#166534', icon: '⚪', iconImage: dotBallsIcon, width: 18, height: 18 },
      { key: 'fiveWicketHauls', label: '5W Hauls', color: '#b45309', icon: '🖐️', iconImage: fiveWicketIcon, width: 22, height: 22 },
    ],
  },
  {
    title: 'Fielding / Keeping',
    items: [
      { key: 'catches', label: 'Catches', color: '#166534', icon: '🧤', iconImage: catchesIcon, width: 22, height: 22 },
      { key: 'runOuts', label: 'Run Outs', color: '#0369a1', icon: '🏃', iconImage: runoutsIcon, width: 18, height: 18 },
      { key: 'stumpings', label: 'Stumpings', color: '#7c3aed', icon: '🥅', iconImage: stumpingsIcon, width: 24, height: 24 },
    ],
  },
  {
    title: 'Awards',
    items: [
      { key: 'potmCount', label: 'POTM', color: '#475569', icon: '🏅', iconImage: potmIcon, width: 18, height: 18 },
      { key: 'mvpCount', label: 'MVP', color: '#7c3aed', icon: '🌟', iconImage: mvpIcon, width: 18, height: 18 },
      { key: 'bestBatsmanCount', label: 'Best Batsman', color: '#b45309', icon: '🏏', iconImage: bestBatsManIcon, width: 18, height: 18 },
      { key: 'bestBowlerCount', label: 'Best Bowler', color: '#0369a1', icon: '🎯', iconImage: bestBowlerIcon, width: 18, height: 18 },
      { key: 'bestFielderCount', label: 'Best Fielder', color: '#166534', icon: '🧤', iconImage: bestFielderIcon, width: 26, height: 26 },
    ],
  },
];

const isPureNumber = (value) => {
  if (typeof value === 'number') return true;
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return /^-?\d+(\.\d+)?$/.test(trimmed);
};

const AnimatedStatValue = ({ value }) => {
  const numeric = isPureNumber(value) ? Number(value) : null;
  const [display, setDisplay] = useState(numeric !== null ? 0 : value);

  useEffect(() => {
    if (numeric === null) {
      setDisplay(value);
      return undefined;
    }

    const duration = 700;
    const start = performance.now();

    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = Number.isInteger(numeric)
        ? Math.round(numeric * eased)
        : (numeric * eased).toFixed(2);

      setDisplay(nextValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplay(Number.isInteger(numeric) ? numeric : numeric.toFixed(2));
      }
    };

    const frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [numeric, value]);

  return <>{display}</>;
};

const PlayerPreview = ({
                         profile,
                         socialStats,
                         ratings,
                         ratingActions,
                         socialLoading = false,
                         onOpenFollowers,
                         onOpenFollowing,
                         headerAction,
                         isDemoProfile = false,
                         demoTooltip = 'This is a demo profile.',
                         showFollowAction = false,
                         debugMode = false,
                       }) => {
  const safe = useMemo(() => {
    const p = profile || {};
    return {
      personal: p.personal || {},
      skills: p.skills || {},
      bio: p.bio || {},
      stats: selectCanonicalProfileStats(p),
      team: p.team || {},
      media: p.media || {},
      addOns: p.addOns || {},
      highlights: p.highlights || [],
      achievements: p.achievements || [],
      milestones: p.milestones || [],
      awardsList: p.awards || [],
      badges: p.badges || [],
      level: p.level || {},
      matchParticipation: p.matchParticipation || { summary: {} },
      preferences: p.preferences || {},
      mediaGallery: p.mediaGallery || [],
      extraFeatures: Array.isArray(p.extraFeatures) ? p.extraFeatures : [],
    };
  }, [profile]);

  const statsData = useMemo(() => {
    if (!profile) return [];

    const profileStats = selectCanonicalProfileStats(profile);

    const mapped = STAT_GROUPS.map((group) => ({
      ...group,
      items: group.items.map(({ key, label, color, icon, iconImage, width, height }) => {
        const rawValue =
          key === 'potmCount'
            ? profileStats?.potmCount ?? profileStats?.playerOfTheMatchCount ?? profileStats?.awards
            : profileStats?.[key];

        return {
          key,
          label,
          color,
          icon,
          iconImage,
          width,
          height,
          displayValue: formatProfileStatValue(key, rawValue),
        };
      }),
    }));


    return mapped;
  }, [profile]);

  const avatarSrc = safe.personal.photoUrl || safe.media.profilePhoto || defaultAvatar;
  const fullName = safe.personal.fullName || 'Unnamed player';
  const city = safe.personal.city || 'City TBD';
  const country = safe.personal.country ? `, ${safe.personal.country}` : '';

  const primaryRole = safe.skills.primaryRole;
  const battingStyle = safe.skills.battingStyle;
  const bowlingStyle = safe.skills.bowlingStyle;

  const expYears = safe.bio.experience || 0;
  const intro = safe.bio.intro || 'Add a short introduction.';
  const strengths = safe.bio.strengths || 'Batting, bowling, fielding';

  const navigate = useNavigate();
  const handleOpenFollowers = onOpenFollowers || (() => navigate('/profile/followers'));
  const handleOpenFollowing = onOpenFollowing || (() => navigate('/profile/following'));

  return (
    <div className="card shadow-sm border-0 h-100">
      <div className="card-body">
        <Section
          eyebrow="Live preview"
          title="Profile summary"
          description="Save to lock-in details and start accepting match invites."
          action={headerAction}
        >
          {profile ? (
            <div className="pt-2">
              <div className="p-3 p-md-4 rounded-4 border bg-white mb-4">
                <div className="d-flex flex-column flex-lg-row gap-4 align-items-start">
                  <div className="d-flex gap-3 flex-grow-1">
                    <div
                      className="rounded-circle overflow-hidden border"
                      style={{ width: 72, height: 72, flex: '0 0 72px' }}
                    >
                      <img
                        src={avatarSrc}
                        alt={fullName}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>

                    <div className="flex-grow-1">
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <h3 className="h5 mb-0">{fullName}</h3>
                        <span className="badge rounded-pill text-bg-light border">
                          {expYears} yrs exp
                        </span>
                        <span className="badge rounded-pill text-bg-success">
                          Match-ready
                        </span>
                      </div>

                      <div className="text-muted small mt-1 d-flex align-items-center gap-2">
                        <span
                          className="rounded-circle"
                          style={{
                            width: 8,
                            height: 8,
                            background: '#1FA2FF',
                            display: 'inline-block',
                          }}
                        />
                        <span>
                          {city}
                          {country}
                        </span>
                      </div>

                      <div className="d-flex flex-wrap gap-2 mt-3">
                        {primaryRole && (
                          <span className="badge rounded-pill text-bg-primary">
                            {primaryRole}
                          </span>
                        )}
                        {battingStyle && (
                          <span className="badge rounded-pill text-bg-secondary">
                            {battingStyle}
                          </span>
                        )}
                        {bowlingStyle && (
                          <span className="badge rounded-pill text-bg-warning">
                            {bowlingStyle}
                          </span>
                        )}
                        {!primaryRole && !battingStyle && !bowlingStyle && (
                          <span className="badge rounded-pill text-bg-light border">
                            Add role & styles
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <ProfileSocialStats
                    visitsCount={socialStats?.visitsCount}
                    followersCount={socialStats?.followersCount}
                    followingCount={socialStats?.followingCount}
                    ratings={ratings}
                    ratingActions={ratingActions}
                    isLoading={socialLoading}
                    onOpenFollowers={handleOpenFollowers}
                    onOpenFollowing={handleOpenFollowing}
                    disableInteractions={isDemoProfile}
                    disabledReason={demoTooltip}
                  />

                  {showFollowAction && (
                    <div className="mt-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        disabled={isDemoProfile}
                        title={isDemoProfile ? demoTooltip : undefined}
                      >
                        Follow
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="row g-3 align-items-stretch mb-4">
                <div className="col-lg-12">
                  <div className="h-100 p-3 p-md-4 rounded-4 border bg-white">
                    <div className="small text-uppercase fw-semibold text-primary mb-3">
                      Stats breakdown
                    </div>

                    {statsData.map((group) => (
                      <div className="mb-4" key={group.title}>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: '1rem',
                              color: '#0f172a',
                            }}
                          >
                            {group.title}
                          </div>
                          <div
                            style={{
                              height: 1,
                              flex: 1,
                              marginLeft: 12,
                              background:
                                'linear-gradient(to right, rgba(148,163,184,0.35), rgba(148,163,184,0.02))',
                            }}
                          />
                        </div>

                        <div className="row g-3">
                          {group.items.map((stat) => (
                            <div className="col-6 col-md-4 col-xl-3" key={stat.key}>
                              <div
                                className="h-100 rounded-4 border bg-white px-3 py-3 stat-card-clean"
                                style={{
                                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                                  transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                                }}
                              >
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                  <div
                                    className="d-inline-flex align-items-center justify-content-center rounded-3"
                                    style={{
                                      width: 40,
                                      height: 40,
                                      backgroundColor: `${stat.color}15`,
                                      border: `1px solid ${stat.color}22`,
                                    }}
                                  >
                                    {stat.iconImage ? (
                                      <img
                                        src={stat.iconImage}
                                        alt={stat.label}
                                        width={stat.width}
                                        height={stat.height}
                                        style={{ opacity: 0.92 }}
                                      />
                                    ) : (
                                      <span style={{ fontSize: 16 }}>{stat.icon}</span>
                                    )}
                                  </div>

                                  <span
                                    className="rounded-pill"
                                    style={{
                                      width: 8,
                                      height: 8,
                                      backgroundColor: stat.color,
                                      display: 'inline-block',
                                    }}
                                  />
                                </div>

                                <div
                                  className="fw-bold lh-1"
                                  style={{
                                    fontSize: '1.7rem',
                                    color: '#0f172a',
                                    letterSpacing: '-0.03em',
                                  }}
                                >
                                  <AnimatedStatValue value={stat.displayValue} />
                                </div>

                                <div
                                  className="mt-2"
                                  style={{
                                    fontSize: '0.88rem',
                                    color: '#64748b',
                                    fontWeight: 500,
                                  }}
                                >
                                  {stat.label}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div className="text-muted small mt-3">
                      Stats are auto-updated from matches you play.
                    </div>

                    {debugMode && (
                      <div className="alert alert-warning mt-3 mb-0 py-2" role="status">
                        Debug mode available. Stats display the backend canonical statsAuto response.
                      </div>
                    )}

                    {!statsData.length && (
                      <div className="text-muted small">No stats available.</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-lg-6">
                  <div className="h-100 p-3 p-md-4 rounded-4 border bg-white">
                    <div className="d-flex align-items-start justify-content-between mb-3">
                      <div>
                        <div className="small text-uppercase fw-semibold text-primary">
                          Bio
                        </div>
                        <div className="h6 mb-0">Player story</div>
                      </div>
                      <span className="badge rounded-pill text-bg-light border">
                        <img src={bioIcon} alt="Bio" width={20} />
                      </span>
                    </div>

                    <div className="p-3 rounded-4 bg-light border">
                      <div className="fw-semibold mb-1">{intro}</div>
                      <div className="text-muted small">
                        Strengths: {strengths}
                      </div>
                      {safe.bio.nickname && (
                        <div className="mt-2">
                          <span className="badge rounded-pill text-bg-light border">
                            Known as {safe.bio.nickname}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="h-100 p-3 p-md-4 rounded-4 border bg-white">
                    <div className="d-flex align-items-start justify-content-between mb-3">
                      <div>
                        <div className="small text-uppercase fw-semibold text-primary">
                          Organization, club, community
                        </div>
                        <div className="h6 mb-0">Affiliation context</div>
                      </div>
                      <span className="badge rounded-pill text-bg-light border">
                        <img src={teamClubIcon} alt="Team Club Icon" width={20} />
                      </span>
                    </div>

                    <ul className="list-unstyled mb-0">
                      <li className="d-flex justify-content-between gap-3 py-2 border-bottom">
                        <span className="text-muted small">Type</span>
                        <span className="fw-semibold text-end text-capitalize">
                          {safe.team.affiliationType || 'Not provided'}
                        </span>
                      </li>
                      <li className="d-flex justify-content-between gap-3 py-2 border-bottom">
                        <span className="text-muted small">Name</span>
                        <span className="fw-semibold text-end">
                          {safe.team.affiliationDetails?.name || 'Not provided'}
                        </span>
                      </li>
                      <li className="d-flex justify-content-between gap-3 py-2 border-bottom">
                        <span className="text-muted small">Role</span>
                        <span className="fw-semibold text-end text-capitalize">
                          {safe.team.affiliationDetails?.role || 'Not provided'}
                        </span>
                      </li>
                      <li className="d-flex justify-content-between gap-3 py-2">
                        <span className="text-muted small">Department</span>
                        <span className="fw-semibold text-end">
                          {safe.team.affiliationDetails?.department || 'Not provided'}
                        </span>
                      </li>
                    </ul>

                    <div className="text-muted small mt-3">
                      Add your affiliation details to help personalize team, club, and community experiences.
                    </div>
                  </div>
                </div>
              </div>

              <ProfileAddOnsView
                addOns={safe.addOns}
                highlights={safe.highlights}
                achievements={safe.achievements}
                milestones={safe.milestones}
                awards={safe.awardsList}
                badges={safe.badges}
                level={safe.level}
                matchParticipation={safe.matchParticipation}
                mediaGallery={safe.mediaGallery}
              />
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-uppercase text-primary fw-semibold mb-1 small">
                No profile yet
              </p>
              <p className="text-muted mb-0">
                Fill in the form to preview a complete player profile. Your inputs
                will populate this summary instantly after you click “Create player profile”.
              </p>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

export default PlayerPreview;
